/**
 * Holding up the card you just drew.
 *
 * The rules for spotting it live in `arrival.ts` and are tested there; this is
 * the React half - when to look, how long to hold, and how to let a player cut
 * the hold short.
 *
 * ## Why it hangs off the move counter rather than off the hand
 *
 * The hand changes for all sorts of reasons: a deal, a discard, somebody else
 * making you pick up. Watching it alone would reveal a card every time, so the
 * question asked here is narrower - *this* seat just made a move the layout
 * declares as travelling to a seat - and only then is the hand compared.
 *
 * The layout is what says which move that is. Hard-coding `drawCard` would
 * work for the three games that ship and for none that a designer writes
 * later, and the table already reads the same `motions` list to decide which
 * moves fly across the felt. One declaration, two features.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { arrivals, FLIGHT_MS, FRESH_MS, REVEAL_MS } from './arrival.js';
import { useCounterEvent } from './use-counter-event.js';

/** The shape of `MatchView.lastMove`, named here so `render/` stays off `net/`. */
interface Move {
  readonly n: number;
  readonly seat: number;
  readonly move: string;
}

export interface Arrival {
  /**
   * The card that is on its way, from the moment it is spotted.
   *
   * Set a whole flight earlier than `reveal`, because it answers a different
   * question: not "what should be held up" but "what must NOT be in the hand
   * yet". The card is in the hand the instant the packet lands - the server
   * has no idea an animation is playing - so without this a player watched
   * their new card sit in the fan while a copy of it was still crossing the
   * table towards them, and then a third time held up over the row.
   */
  readonly arriving: string | null;
  /**
   * The one card to hold up, or null.
   *
   * Null when several arrived at once: a penalty that deals you four cards has
   * no single card to show, and four held up in turn would be a cutscene. Those
   * get the mark in the hand and nothing else.
   */
  readonly reveal: string | null;
  /** Cards to mark in the hand, so you can see where the new one went. */
  readonly fresh: Set<string>;
  /** Ends the hold early, for a player who has already read the card. */
  readonly dismiss: () => void;
}

const NOTHING: Set<string> = new Set();

export function useArrival(
  hand: readonly string[],
  move: Move | null | undefined,
  seat: number | null,
  travelsToSeat: (move: string) => boolean,
): Arrival {
  const [arriving, setArriving] = useState<string | null>(null);
  const [reveal, setReveal] = useState<string | null>(null);
  const [fresh, setFresh] = useState<Set<string>>(NOTHING);

  /*
   * What the hand was BEFORE this render.
   *
   * Kept honest by the effect at the bottom, which runs after every render and
   * so tracks changes nobody asked about - a deal, a card played. Without that
   * the next draw would report everything that had happened since the last one.
   */
  const previous = useRef<readonly string[]>(hand);

  // Same reason `useFlights` does it: these are rebuilt every render, and
  // naming them would re-run the effect and cancel its timers.
  const latest = useRef({ hand, move, seat, travelsToSeat });
  latest.current = { hand, move, seat, travelsToSeat };

  /*
   * Cleared on unmount, so a player who leaves the table mid-reveal does not
   * have `setState` called on them a second later.
   */
  const timers = useRef<number[]>([]);
  const after = useCallback((ms: number, run: () => void) => {
    timers.current.push(window.setTimeout(run, ms));
  }, []);
  useEffect(
    () => () => {
      for (const timer of timers.current) window.clearTimeout(timer);
    },
    [],
  );

  useCounterEvent(move?.n ?? 0, () => {
    const at = latest.current;
    if (!at.move || at.seat === null) return;
    // Somebody else's draw is somebody else's business - and their card is not
    // in this hand to be found anyway.
    if (at.move.seat !== at.seat) return;
    if (!at.travelsToSeat(at.move.move)) return;

    const added = arrivals(previous.current, at.hand);
    if (added.length === 0) return;

    /*
     * Held until the flown card has landed. The card is already in the hand by
     * now - the view and the move arrive in the same packet - so showing it
     * immediately would mean a card face-up over the hand while a face-down one
     * was still crossing the table towards it.
     */
    if (added.length === 1) {
      const card = added[0] ?? null;
      // Hidden from the fan at once, shown in the fan when the hold ends: one
      // card, in one place, at every moment.
      setArriving(card);
      after(FLIGHT_MS, () => setReveal(card));
      // Clears this card rather than whatever is up: two draws in quick
      // succession would otherwise have the first one's timer close the
      // second one's reveal a moment after it opened.
      after(FLIGHT_MS + REVEAL_MS, () => {
        setReveal((current) => (current === card ? null : current));
        setArriving((current) => (current === card ? null : current));
      });
    }

    const marked = new Set(added);
    setFresh(marked);
    after(FLIGHT_MS + REVEAL_MS + FRESH_MS, () => {
      setFresh((current) => (current === marked ? NOTHING : current));
    });
  });

  // Declared after the counter event on purpose: effects run in order, so that
  // one still sees the old hand and this one then catches up.
  useEffect(() => {
    previous.current = hand;
  }, [hand]);

  /*
   * Ends the hold AND drops the card into the hand, in the same tick.
   *
   * Clearing only the reveal would take the card off the screen entirely
   * until the timer caught up - a player who tapped to hurry things along
   * would watch their new card vanish.
   */
  const dismiss = useCallback(() => {
    setReveal(null);
    setArriving(null);
  }, []);

  return { arriving, reveal, fresh, dismiss };
}
